import React, { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  Upload,
  Button,
  Steps,
  Typography,
  Table,
  Alert,
  Space,
  Progress,
  Result,
  Divider,
  Card,
  Row,
  Col,
  Tag,
  message,
  Tooltip,
  Checkbox,
} from 'antd';
import * as XLSX from 'xlsx';
import {
  UploadOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import apiService from '../../../services/apiService';
import { useTranslation } from "react-i18next";

const { Title, Text, Paragraph } = Typography;
const { Step } = Steps;

interface ValidationError {
  row: number;
  field: string;
  value: any;
  error: string;
  severity: 'error' | 'warning';
}

interface PreviewItem {
  row: number;
  groupId: string;
  customerName: string;
  warehouseName: string;
  customerCode: string;
  productNameEN: string;
  productNameCN: string;
  skuId: string;
  deliveryTrackingNumber: string;
  quantityPackage: number;
  quantityBaseUnit: number;
  unitPrice: number;
  courier: string;
  receiver: string;
  deliveryAddress: string;
  // 訂單備註 / 配送備註. Both order-level, so they are read from the first row
  // of each groupId.
  remark: string;
  deliveryRemark: string;
  // Offered in the upload dialog and read from the sheet, but previously dropped
  // here. The backend accepts every one of them: countryCode on the order,
  // the rest on each line item.
  countryCode: string;
  barcodePackage: string;
  barcodeBaseUnit: string;
  brand: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  allocateRule: string;
  status: 'valid' | 'error' | 'warning';
  errors: ValidationError[];
}

interface BulkUploadModalProps {
  visible: boolean;
  onCancel: () => void;
  onUploadComplete: (data: any[]) => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  visible,
  onCancel,
  onUploadComplete,
}) => {
    const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [previewData, setPreviewData] = useState<PreviewItem[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // 重置狀態
  const resetState = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setIsProcessing(false);
    setUploadProgress(0);
  };

  // 當模態框打開時重置狀態
  useEffect(() => {
    if (visible) {
      resetState();
    }
  }, [visible]);

  // 模擬的CSV模板數據 - 包含多SKU訂單示例
  const templateData = [
    {
      groupId: '1',
      customerName: 'test',
      warehouseName: 'KC',
      customerCode: 'CUST001',
      productNameEN: 'Wireless Headphones',
      productNameCN: '無線耳機',
      skuId: 'WH-001',
      deliveryTrackingNumber: 'SF1234567890',
      barcodePackage: '1234567890123',
      barcodeBaseUnit: '1234567890124',
      quantityPackage: '10',
      quantityBaseUnit: '10',
      brand: 'TechBrand',
      length: '20',
      width: '15',
      height: '8',
      weight: '0.5',
      unitPrice: '99.99',
      courier: 'DHL',
      receiver: '李先生',
      countryCode: 'CN',
      deliveryAddress: '廣東省深圳市南山區科技園',
      deliveryRemark: '',
      allocateRule: 'FIFO',
      remark: '測試訂單',
      status: '待發貨'
    },
    {
      groupId: '1',
      customerName: 'test',
      warehouseName: 'KC',
      customerCode: 'CUST001',
      productNameEN: 'USB Cable',
      productNameCN: 'USB數據線',
      skuId: 'USB-002',
      deliveryTrackingNumber: 'SF1234567890',
      barcodePackage: '',
      barcodeBaseUnit: '',
      quantityPackage: '5',
      quantityBaseUnit: '5',
      brand: 'TechBrand',
      length: '',
      width: '',
      height: '',
      weight: '',
      unitPrice: '19.99',
      courier: 'DHL',
      receiver: '李先生',
      countryCode: 'CN',
      deliveryAddress: '廣東省深圳市南山區科技園',
      deliveryRemark: '',
      allocateRule: 'FIFO',
      remark: '測試訂單',
      status: '待發貨'
    }
  ];

  // 字段說明
  const fieldDescriptions = [
    { field: 'groupId', name: '分組ID', required: true, description: t('相同分組ID的多行會合併為一個訂單（如: 1, 2, 3...）') },
    { field: 'customerName', name: '客戶名稱', required: true, description: t('客戶的完整名稱') },
    { field: 'warehouseName', name: '倉庫名稱', required: true, description: t('倉庫的完整名稱') },
    { field: 'skuId', name: 'SKU ID', required: true, description: t('產品的SKU識別碼') },
    { field: 'deliveryTrackingNumber', name: '配送追蹤號', required: false, description: t('快遞追蹤號碼（可留空）') },
    { field: 'customerCode', name: '客戶代碼', required: false, description: t('客戶的唯一識別代碼') },
    { field: 'productNameEN', name: '產品名稱(英文)', required: false, description: t('產品的英文名稱') },
    { field: 'productNameCN', name: '產品名稱(中文)', required: false, description: t('產品的中文名稱') },
    { field: 'barcodePackage', name: '包裝條碼', required: false, description: t('包裝級別的條碼') },
    { field: 'barcodeBaseUnit', name: '基本單位條碼', required: false, description: t('基本單位的條碼') },
    { field: 'quantityPackage', name: '包裝數量', required: false, description: t('包裝單位的數量') },
    { field: 'quantityBaseUnit', name: '基本單位數量', required: false, description: t('基本單位的數量') },
    { field: 'brand', name: '品牌', required: false, description: t('產品品牌') },
    { field: 'length', name: '長度', required: false, description: t('產品長度(cm)') },
    { field: 'width', name: '寬度', required: false, description: t('產品寬度(cm)') },
    { field: 'height', name: '高度', required: false, description: t('產品高度(cm)') },
    { field: 'weight', name: '重量', required: false, description: t('產品重量(kg)') },
    { field: 'unitPrice', name: '單價', required: false, description: t('產品單價') },
    { field: 'courier', name: '快遞商', required: false, description: t('配送快遞公司') },
    { field: 'receiver', name: '收件人', required: false, description: t('收件人姓名') },
    { field: 'countryCode', name: '國家或地區', required: false, description: t('收件地址國家或地區代碼') },
    { field: 'deliveryAddress', name: '配送地址', required: false, description: t('詳細配送地址') },
    { field: 'deliveryRemark', name: '配送備註', required: false, description: t('配送相關備註') },
    { field: 'allocateRule', name: '分配規則', required: false, description: 'FIFO/LIFO/FEFO/MANUAL' },
    { field: 'remark', name: '備註', required: false, description: t('訂單備註') },
    { field: 'status', name: '狀態', required: false, description: t('訂單狀態') },
  ];

  // 可選字段勾選狀態（依賴 fieldDescriptions）
  const optionalDefaultKeys = useMemo(
    () => fieldDescriptions.filter(f => !f.required).map(f => f.field),
    [fieldDescriptions]
  );
  const [selectedOptionalKeys, setSelectedOptionalKeys] = useState<string[]>(optionalDefaultKeys);

  // 下載模板 - Excel格式
  const downloadTemplate = () => {
    // 必填在前，可選依勾選
    const required = fieldDescriptions.filter(f => f.required);
    const optional = fieldDescriptions.filter(f => !f.required && selectedOptionalKeys.includes(f.field));
    const selected = [...required, ...optional];
    
    // 構建Excel數據
    const headers = selected.map(field => field.name);
    const data = [
      headers,
      ...templateData.map(row => selected.map(field => row[field.field] || ''))
    ];

    // 創建工作簿和工作表
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '出庫訂單模板');

    // 設置列寬
    const colWidths = selected.map(() => ({ wch: 15 }));
    worksheet['!cols'] = colWidths;

    // 下載Excel文件
    XLSX.writeFile(workbook, '出庫訂單批量上傳模板.xlsx');
    message.success(t('Excel模板下載成功'));
  };

  // 文件上傳配置
  const uploadProps: UploadProps = {
    name: 'file',
    accept: '.csv,.xlsx,.xls',
    showUploadList: false,
    beforeUpload: (file) => {
      setUploadedFile(file);
      processFile(file);
      return false;
    },
  };

  // 將 Excel 日期序列號轉換為 YYYY-MM-DD 格式
  const convertExcelDate = (value: any): string => {
    if (!value) return '';
    
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    
    if (typeof value === 'number') {
      const excelEpoch = new Date(1899, 11, 30);
      const date = new Date(excelEpoch.getTime() + value * 86400000);
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
    
    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    }
    
    const str = String(value).trim();
    const patterns = [
      /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/,
      /^(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/,
    ];
    
    for (const pattern of patterns) {
      const match = str.match(pattern);
      if (match) {
        let year, month, day;
        
        if (match[1].length === 4) {
          year = match[1];
          month = match[2].padStart(2, '0');
          day = match[3].padStart(2, '0');
        } else {
          day = match[1].padStart(2, '0');
          month = match[2].padStart(2, '0');
          year = match[3];
        }
        
        return `${year}-${month}-${day}`;
      }
    }
    
    return str;
  };

  // 處理文件
  const processFile = async (file: UploadFile) => {
    setIsProcessing(true);
    setUploadProgress(0);

    try {
      const fileName = (file as any).name || '';
      const isExcelFile = fileName.endsWith('.xlsx') || fileName.endsWith('.xls');
      
      let parsedData: any[];
      
      if (isExcelFile) {
        console.log('檢測到 Excel 文件，使用 XLSX 庫解析');
        const fileContent = await readFileAsArrayBuffer(file);
        setUploadProgress(30);
        parsedData = parseExcelContent(fileContent);
        setUploadProgress(60);
      } else {
        console.log('檢測到 CSV 文件，使用文本解析');
        const fileContent = await readFileContent(file);
        setUploadProgress(30);
        parsedData = parseCSVContent(fileContent);
        setUploadProgress(60);
      }

      // 驗證數據
      const validatedData = validateParsedData(parsedData);
      setUploadProgress(90);

      // 生成驗證錯誤列表
      const errors = generateValidationErrors(validatedData);
      
      setPreviewData(validatedData);
      setValidationErrors(errors);
      setUploadProgress(100);
      
      setIsProcessing(false);
      setCurrentStep(1);
    } catch (error) {
      console.error('文件處理錯誤:', error);
      const errorMessage = error instanceof Error ? error.message : t('未知錯誤');
      
      if (errorMessage.includes('格式不正確')) {
        message.error({
          content: t('文件格式錯誤！請確保：1) 文件包含標題行和數據行；2) 如果用Excel編輯過，請另存為CSV (UTF-8) 格式或直接上傳 .xlsx 文件'),
          duration: 6
        });
      } else {
        message.error(t('文件處理失敗：{{errorMessage}}', { errorMessage }));
      }
      
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  // 读取文件內容（文本格式 - 用於 CSV）
  const readFileContent = (file: UploadFile): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        let content = e.target?.result as string;
        
        // 移除 BOM 字符（UTF-8, UTF-16 LE, UTF-16 BE）
        content = content.replace(/^\uFEFF/, ''); // UTF-8 BOM
        content = content.replace(/^\uFFFE/, ''); // UTF-16 LE BOM
        
        console.log('CSV 文件前100個字符:', content.substring(0, 100));
        console.log('CSV 文件第一行:', content.split('\n')[0]);
        
        resolve(content);
      };
      reader.onerror = () => reject(new Error(t('文件读取失敗')));
      reader.readAsText(file as File, 'UTF-8');
    });
  };

  // 讀取文件內容（二進制格式 - 用於 Excel）
  const readFileAsArrayBuffer = (file: UploadFile): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as ArrayBuffer;
        resolve(content);
      };
      reader.onerror = () => reject(new Error(t('文件讀取失敗')));
      reader.readAsArrayBuffer(file as unknown as File);
    });
  };

  // 解析 Excel 內容
  const parseExcelContent = (arrayBuffer: ArrayBuffer): any[] => {
    try {
      const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: true });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: false }) as any[][];
      
      if (jsonData.length < 2) {
        throw new Error('Excel 文件格式不正確，至少需要標題行和一行數據');
      }
      
      const headers = jsonData[0].map((h: any) => String(h || '').trim());
      
      // 識別日期字段
      const dateFields = ['deliveryDate', 'expectedDeliveryDate', 'requestedDeliveryDate'];
      const dateFieldIndices = headers.map((header, index) => {
        const fieldKey = getFieldKey(header);
        return dateFields.includes(fieldKey) ? index : -1;
      }).filter(index => index !== -1);
      
      const data: any[] = [];
      for (let i = 1; i < jsonData.length; i++) {
        const row: any = { row: i };
        const values = jsonData[i];
        
        headers.forEach((header, index) => {
          const fieldKey = getFieldKey(header);
          let value = values[index];
          
          if (dateFieldIndices.includes(index)) {
            value = convertExcelDate(value);
          } else {
            value = String(value || '').trim();
          }
          
          row[fieldKey] = value;
        });
        
        data.push(row);
      }
      
      console.log(`成功解析 Excel 文件：${headers.length} 個欄位，${data.length} 行數據`);
      return data;
    } catch (error) {
      console.error('Excel 解析錯誤:', error);
      throw new Error(t('Excel 文件解析失敗：') + (error instanceof Error ? error.message : t('未知錯誤')));
    }
  };

  // 自動檢測CSV分隔符
  const detectDelimiter = (content: string): string => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) return ',';
    
    const firstLine = lines[0];
    const delimiters = [',', ';', '\t', '|'];
    const counts = delimiters.map(delim => ({
      delimiter: delim,
      count: firstLine.split(delim).length
    }));
    
    counts.sort((a, b) => b.count - a.count);
    return counts[0].count > 1 ? counts[0].delimiter : ',';
  };

  // 解析CSV內容
  const parseCSVContent = (content: string): any[] => {
    // 移除BOM字符（多種BOM格式）
    content = content.replace(/^\uFEFF/, ''); // UTF-8 BOM
    content = content.replace(/^\uFFFE/, ''); // UTF-16 LE BOM
    content = content.replace(/^\ufeff/, ''); // 小寫版本
    
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length < 2) {
      throw new Error('CSV文件格式不正確，至少需要標題行和一行數據');
    }

    const delimiter = detectDelimiter(content);
    console.log('檢測到的CSV分隔符:', delimiter === ',' ? '逗號' : delimiter === ';' ? '分號' : delimiter === '\t' ? '制表符' : delimiter);

    const headers = lines[0].split(delimiter).map(header => header.trim().replace(/"/g, '').replace(/\r/g, ''));
    
    console.log('解析到的標題行:', headers);
    console.log('標題行映射結果:', headers.map(h => `"${h}" -> "${getFieldKey(h)}"`));
    
    // 識別日期字段
    const dateFields = ['deliveryDate', 'expectedDeliveryDate', 'requestedDeliveryDate'];
    const dateFieldIndices = headers.map((header, index) => {
      const fieldKey = getFieldKey(header);
      return dateFields.includes(fieldKey) ? index : -1;
    }).filter(index => index !== -1);
    
    const data: any[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = parseCSVLine(lines[i], delimiter);
      
      // 即使字段數量不匹配也嘗試解析，只是給出警告
      if (values.length !== headers.length) {
        console.warn(`第${i+1}行字段數量不匹配: 期望${headers.length}個字段，實際${values.length}個字段`);
      }
      
      const row: any = { row: i };
      headers.forEach((header, index) => {
        const fieldKey = getFieldKey(header);
        let value = (index < values.length ? values[index] : '')?.trim() || '';
        
        if (dateFieldIndices.includes(index) && value) {
          value = convertExcelDate(value);
        }
        
        row[fieldKey] = value;
      });
      
      // 調試：打印第一行數據的映射結果
      if (i === 1) {
        console.log('第一行數據解析結果:', row);
      }
      
      data.push(row);
    }

    console.log(`成功解析 CSV 文件：${headers.length} 個欄位，${data.length} 行數據`);
    return data;
  };

  // 解析CSV行（處理引號和自定義分隔符）
  const parseCSVLine = (line: string, delimiter: string = ','): string[] => {
    line = line.replace(/\r?\n$/, '');
    
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    let i = 0;
    
    while (i <= line.length) {
      const char = i < line.length ? line[i] : delimiter;
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === delimiter && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else if (i < line.length) {
        current += char;
      }
      
      i++;
    }
    
    result.push(current);
    return result.map(val => val.replace(/"/g, ''));
  };

  // 将中文字段名映射到字段键
  const getFieldKey = (header: string): string => {
    // 清理標題字符串：移除所有不可見字符和多餘空格
    const cleanHeader = header
      .trim()
      .replace(/[\u200B-\u200D\uFEFF]/g, '') // 移除零寬字符
      .replace(/\s+/g, '') // 移除所有空格
      .replace(/[^\u4e00-\u9fa5a-zA-Z0-9()（）]/g, ''); // 只保留中文、英文、數字和括號
    
    const fieldMap: Record<string, string> = {
      '分組ID': 'groupId',
      '分组ID': 'groupId',
      'GroupID': 'groupId',
      'groupId': 'groupId',
      '客戶名稱': 'customerName',
      '客户名称': 'customerName',
      '倉庫名稱': 'warehouseName',
      '仓库名称': 'warehouseName',
      '客戶代碼': 'customerCode',
      '客户代码': 'customerCode',
      '產品名稱(英文)': 'productNameEN',
      '產品名稱（英文）': 'productNameEN',
      '产品名称(英文)': 'productNameEN',
      '产品名称（英文）': 'productNameEN',
      '產品名稱(中文)': 'productNameCN',
      '產品名稱（中文）': 'productNameCN',
      '产品名称(中文)': 'productNameCN',
      '产品名称（中文）': 'productNameCN',
      'SKUID': 'skuId',
      '包裝條碼': 'barcodePackage',
      '包装条码': 'barcodePackage',
      '基本單位條碼': 'barcodeBaseUnit',
      '基本单位条码': 'barcodeBaseUnit',
      '包裝數量': 'quantityPackage',
      '包装数量': 'quantityPackage',
      '基本單位數量': 'quantityBaseUnit',
      '基本单位数量': 'quantityBaseUnit',
      '品牌': 'brand',
      '長度': 'length',
      '长度': 'length',
      '寬度': 'width',
      '宽度': 'width',
      '高度': 'height',
      '重量': 'weight',
      '單價': 'unitPrice',
      '单价': 'unitPrice',
      '快遞商': 'courier',
      '快递商': 'courier',
      '收件人': 'receiver',
      '國家或地區': 'countryCode',
      '国家或地区': 'countryCode',
      '配送地址': 'deliveryAddress',
      '追蹤號': 'deliveryTrackingNumber',
      '追踪号': 'deliveryTrackingNumber',
      '配送追蹤號': 'deliveryTrackingNumber',
      '配送追踪号': 'deliveryTrackingNumber',
      '配送備註': 'deliveryRemark',
      '配送备注': 'deliveryRemark',
      '分配規則': 'allocateRule',
      '分配规则': 'allocateRule',
      '備註': 'remark',
      '备注': 'remark',
      '狀態': 'status',
      '状态': 'status'
    };
    
    // 首先嘗試使用清理後的標題匹配
    if (fieldMap[cleanHeader]) {
      return fieldMap[cleanHeader];
    }
    
    // 如果清理後的標題沒有匹配，嘗試原始標題
    if (fieldMap[header.trim()]) {
      return fieldMap[header.trim()];
    }
    
    // 如果都沒有匹配，返回清理後的標題
    return cleanHeader || header;
  };

  // 驗證解析的數據
  const validateParsedData = (data: any[]): PreviewItem[] => {
    return data.map((row, index) => {
      const errors: ValidationError[] = [];
      let status: 'valid' | 'warning' | 'error' = 'valid';

      // 必填字段驗證
      const requiredFields = [
        { key: 'groupId', name: t('分組ID') },
        { key: 'customerName', name: t('客戶名稱') },
        { key: 'warehouseName', name: t('倉庫名稱') },
        { key: 'skuId', name: 'SKU ID' }
      ];

      requiredFields.forEach(field => {
        if (!row[field.key] || row[field.key].toString().trim() === '') {
          errors.push({
            row: index + 1,
            field: field.key,
            value: row[field.key],
            error: t('{{fieldName}}不能為空', { fieldName: field.name }),
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 數字字段驗證
      const numberFields = ['quantityPackage', 'quantityBaseUnit', 'unitPrice'];
      numberFields.forEach(field => {
        if (row[field] && isNaN(Number(row[field]))) {
          errors.push({
            row: index + 1,
            field: field,
            value: row[field],
            error: t('{{field}}必須為數字', { field }),
            severity: 'error'
          });
          status = 'error';
        }
      });

      // 庫存警告（模拟）
      if (row.quantityBaseUnit && Number(row.quantityBaseUnit) > 30) {
        errors.push({
          row: index + 1,
          field: 'quantityBaseUnit',
          value: row.quantityBaseUnit,
          error: t('庫存可能不足，建議檢查'),
          severity: 'warning'
        });
        if (status !== 'error') status = 'warning';
      }

      return {
        row: index + 1,
        groupId: row.groupId || '',
        customerName: row.customerName || '',
        warehouseName: row.warehouseName || '',
        customerCode: row.customerCode || '',
        productNameEN: row.productNameEN || '',
        productNameCN: row.productNameCN || '',
        skuId: row.skuId || '',
        deliveryTrackingNumber: row.deliveryTrackingNumber || '',
        quantityPackage: Number(row.quantityPackage) || 0,
        quantityBaseUnit: Number(row.quantityBaseUnit) || 0,
        unitPrice: Number(row.unitPrice) || 0,
        courier: row.courier || '',
        receiver: row.receiver || '',
        deliveryAddress: row.deliveryAddress || '',
        remark: row.remark || '',
        deliveryRemark: row.deliveryRemark || '',
        countryCode: row.countryCode || '',
        barcodePackage: row.barcodePackage || '',
        barcodeBaseUnit: row.barcodeBaseUnit || '',
        brand: row.brand || '',
        length: Number(row.length) || 0,
        width: Number(row.width) || 0,
        height: Number(row.height) || 0,
        weight: Number(row.weight) || 0,
        allocateRule: row.allocateRule || '',
        status,
        errors
      };
    });
  };

  // 生成驗證錯誤列表
  const generateValidationErrors = (data: PreviewItem[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    data.forEach(item => {
      errors.push(...item.errors);
    });
    return errors;
  };





  // 確認導入
  const confirmImport = async () => {
    const validData = previewData.filter(item => item.status === 'valid' || item.status === 'warning');

    if (validData.length === 0) {
      message.error(t('沒有有效的數據可以導入'));
      return;
    }

    try {
      // 獲取所有倉庫列表
      const warehousesResp: any = await apiService.getWarehouses({ limit: 1000 });
      const warehouses = warehousesResp?.data || [];

      // 獲取所有客戶列表
      const customersResp: any = await apiService.getCustomers({ limit: 1000 });
      const customers = customersResp?.customers || [];

      // 驗證每個訂單的倉庫名稱和客戶名稱是否存在
      const invalidWarehouses: string[] = [];
      const invalidCustomers: string[] = [];

      // Step 1: 按 groupId 分組（groupId 為必填字段）
      const orderGroups: Record<string, PreviewItem[]> = {};

      validData.forEach(item => {
        const groupKey = item.groupId.trim();
        if (!orderGroups[groupKey]) {
          orderGroups[groupKey] = [];
        }
        orderGroups[groupKey].push(item);
      });

      console.log(`分組結果: ${Object.keys(orderGroups).length} 個訂單，來自 ${validData.length} 行數據`);

      // Step 2: 將每個分組轉換為訂單
      const ordersWithIds: any[] = [];

      for (const [groupKey, items] of Object.entries(orderGroups)) {
        // 使用第一行的訂單級別信息
        const firstItem = items[0];

        // 驗證倉庫名稱（去除左右空格）
        const warehouseName = firstItem.warehouseName?.trim();
        const warehouse = warehouses.find((w: any) => w.name === warehouseName);

        if (!warehouse) {
          if (!invalidWarehouses.includes(warehouseName)) {
            invalidWarehouses.push(warehouseName);
          }
          continue;
        }

        // 驗證客戶名稱（去除左右空格）
        const customerName = firstItem.customerName?.trim();
        const customer = customers.find((c: any) => c.name === customerName);

        if (!customer) {
          if (!invalidCustomers.includes(customerName)) {
            invalidCustomers.push(customerName);
          }
          continue;
        }

        // 合併所有行的產品項目
        const orderItems = items.map(item => ({
          productNameEN: item.productNameEN,
          productNameCN: item.productNameCN,
          skuId: item.skuId,
          quantityPackage: item.quantityPackage,
          quantityBaseUnit: item.quantityBaseUnit,
          unitPrice: item.unitPrice,
          barcodePackage: item.barcodePackage,
          barcodeBaseUnit: item.barcodeBaseUnit,
          brand: item.brand,
          length: item.length,
          width: item.width,
          height: item.height,
          weight: item.weight,
          // Left undefined when the sheet is blank so the backend keeps its own
          // default (the product's batch control type, else FIFO) rather than
          // being handed an empty string.
          allocateRule: item.allocateRule || undefined,
        }));

        ordersWithIds.push({
          orderNumber: '', // Will be auto-generated by backend
          customerName: firstItem.customerName,
          customerId: customer.id,
          customerCode: firstItem.customerCode,
          warehouseId: warehouse.id,
          warehouseName: firstItem.warehouseName,
          items: orderItems,
          courier: firstItem.courier,
          receiver: firstItem.receiver,
          deliveryAddress: firstItem.deliveryAddress,
          deliveryTrackingNumber: firstItem.deliveryTrackingNumber,
          // outboundService.create already stores this: it writes
          // payload.remark into outbound_orders.notes. The column was offered in
          // the upload dialog and parsed from the sheet, then dropped here and in
          // validateParsedData, so 訂單備註 never reached the order.
          remark: firstItem.remark,
          deliveryRemark: firstItem.deliveryRemark,
          countryCode: firstItem.countryCode,
          status: '待發貨',
          hasStockIssues: items.some(item => item.status === 'warning')
        });
      }

      // 收集所有錯誤信息
      const errors: string[] = [];

      if (invalidWarehouses.length > 0) {
        errors.push(
          t('以下倉庫不存在，請先添加對應的倉庫：') + '\n' + invalidWarehouses.map(name => `  • ${name}`).join('\n')
        );
      }

      if (invalidCustomers.length > 0) {
        errors.push(
          t('以下客戶不存在，請先添加對應的客戶：') + '\n' + invalidCustomers.map(name => `  • ${name}`).join('\n')
        );
      }

      // 如果有任何錯誤，顯示統一的錯誤提示
      if (errors.length > 0) {
        message.error({
          content: (
            <div>
              <p style={{ marginBottom: 12, fontWeight: 'bold' }}>{t('批量上傳驗證失敗：')}</p>
              {invalidWarehouses.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <p style={{ marginBottom: 4, color: '#ff4d4f' }}>{t('以下倉庫不存在，請先添加對應的倉庫：')}</p>
                  <ul style={{ marginTop: 4, paddingLeft: 20, marginBottom: 0 }}>
                    {invalidWarehouses.map((name, idx) => (
                      <li key={idx}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {invalidCustomers.length > 0 && (
                <div>
                  <p style={{ marginBottom: 4, color: '#ff4d4f' }}>{t('以下客戶不存在，請先添加對應的客戶：')}</p>
                  <ul style={{ marginTop: 4, paddingLeft: 20, marginBottom: 0 }}>
                    {invalidCustomers.map((name, idx) => (
                      <li key={idx}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ),
          duration: 10
        });
        return;
      }

      // 過濾掉null值
      const validOrders = ordersWithIds.filter(order => order !== null);

      onUploadComplete(validOrders);
      message.success(t('成功導入 {{orderCount}} 個訂單（共 {{itemCount}} 個SKU），列表已自動刷新', { orderCount: validOrders.length, itemCount: validData.length }));
      
      // 直接關閉模態框，不顯示成功頁面
      resetState();
      onCancel();
    } catch (error) {
      console.error('驗證失敗:', error);
      message.error(t('驗證倉庫或客戶時發生錯誤，請稍後重試'));
    }
  };

  // 關閉彈窗
  const handleClose = () => {
    setCurrentStep(0);
    setUploadedFile(null);
    setPreviewData([]);
    setValidationErrors([]);
    setIsProcessing(false);
    setUploadProgress(0);
    onCancel();
  };

  // 預覽表格列定義
  const previewColumns: ColumnsType<PreviewItem> = [
    {
      title: t('行號'),
      dataIndex: 'row',
      width: 60,
      render: (row, record) => (
        <Space>
          <span>{row}</span>
          {record.status === 'error' && <CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
          {record.status === 'warning' && <ExclamationCircleOutlined style={{ color: '#faad14' }} />}
          {record.status === 'valid' && <CheckCircleOutlined style={{ color: '#52c41a' }} />}
        </Space>
      ),
    },
    {
      title: t('分組ID'),
      dataIndex: 'groupId',
      width: 80,
      render: (groupId) => groupId || <Text type="danger">{t('缺少')}</Text>,
    },
    {
      title: t('客戶'),
      key: 'customer',
      width: 150,
      render: (_, record) => (
        <div>
          <div>{record.customerName || <Text type="danger">{t('缺少客戶名稱')}</Text>}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>{record.customerCode}</Text>
        </div>
      ),
    },
    {
      title: t('產品'),
      key: 'product',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.productNameEN}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>{record.productNameCN}</div>
          <div style={{ fontSize: '12px', color: '#999' }}>{record.skuId}</div>
        </div>
      ),
    },
    {
      title: t('數量'),
      key: 'quantity',
      width: 100,
      render: (_, record) => (
        <div>
          <div>{t('包裝:')} {record.quantityPackage}</div>
          <div>{t('基本:')} {record.quantityBaseUnit}</div>
        </div>
      ),
    },
    {
      title: t('單價'),
      dataIndex: 'unitPrice',
      width: 100,
      render: (price) => `¥${price.toFixed(2)}`,
    },
    {
      title: t('收件信息'),
      key: 'delivery',
      render: (_, record) => (
        <div>
          <div>{record.receiver}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            {record.deliveryAddress.length > 20 
              ? `${record.deliveryAddress.substring(0, 20)}...` 
              : record.deliveryAddress}
          </div>
        </div>
      ),
    },
    {
      title: t('狀態'),
      key: 'status',
      width: 100,
      render: (_, record) => {
        let color = 'default';
        let text = t('未知');

        switch (record.status) {
          case 'valid':
            color = 'success';
            text = t('有效');
            break;
          case 'warning':
            color = 'warning';
            text = t('警告');
            break;
          case 'error':
            color = 'error';
            text = t('錯誤');
            break;
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
  ];

  const errorColumns: ColumnsType<ValidationError> = [
    {
      title: t('行號'),
      dataIndex: 'row',
      width: 60,
    },
    {
      title: t('字段'),
      dataIndex: 'field',
      width: 120,
    },
    {
      title: t('值'),
      dataIndex: 'value',
      width: 150,
      render: (value) => value || <Text type="secondary">{t('空值')}</Text>,
    },
    {
      title: t('錯誤信息'),
      dataIndex: 'error',
      render: (error, record) => (
        <Text type={record.severity === 'error' ? 'danger' : 'warning'}>
          {error}
        </Text>
      ),
    },
    {
      title: t('嚴重性'),
      dataIndex: 'severity',
      width: 80,
      render: (severity) => (
        <Tag color={severity === 'error' ? 'red' : 'orange'}>
          {severity === 'error' ? t('錯誤') : t('警告')}
        </Tag>
      ),
    },
  ];

  const steps = [
    {
      title: t('上傳文件'),
      description: t('選擇並上傳CSV/Excel文件'),
    },
    {
      title: t('數據預覽'),
      description: t('查看和驗證數據'),
    },
    {
      title: t('導入完成'),
      description: t('數據導入成功'),
    },
  ];

  return (
    <Modal
      title={t('批量上傳出庫訂單')}
      open={visible}
      onCancel={handleClose}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map(step => (
          <Step key={step.title} title={step.title} description={step.description} />
        ))}
      </Steps>

      {currentStep === 0 && (
        <div>
          <Alert
            message={t('批量上傳說明')}
            description={
              <div>
                <p style={{ margin: 0 }}>{t('支持上傳CSV或Excel文件，系統將自動解析和驗證數據。')}</p>
                <p style={{ margin: '8px 0 0 0', fontWeight: 'bold' }}>
                  {t('分組ID（必填）：使用相同的分組ID（如: 1, 2, 3...）將多行合併為一個訂單，每行代表一個SKU。')}
                                        </p>
              </div>
            }
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />

          <Card title={t('步驟 1: 下載模板')} style={{ marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                {t('請先下載出庫訂單模板文件，模板包含所有必需的字段和格式說明。支持Excel格式（.xlsx），可直接用Microsoft Excel或WPS編輯。')}
                                            </Paragraph>
              <div style={{
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '16px'
              }}>
                <Text strong style={{ color: '#389e0d' }}>{t('✓ 提示')}</Text>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', color: '#666' }}>
                  <li>{t('必填字段不能為空，否則會導致驗證失敗')}</li>
                  <li><strong>{t('分組ID')}</strong>{t('（必填）：使用相同的分組ID（如: 1, 2, 3...）將多行合併為一個訂單')}</li>
                  <li>{t('每個訂單必須有分組ID，相同ID的行會合併為同一訂單的多個SKU')}</li>
                  <li>{t('日期字段請使用 YYYY-MM-DD 格式（如：2025-01-15）')}</li>
                  <li>{t('數量字段請輸入有效的數字')}</li>
                </ul>
              </div>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadTemplate}
                size="large"
              >
                {t('下載Excel模板')}
                                            </Button>
            </Space>
          </Card>

          <Card title={t('步驟 2: 上傳文件')}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Paragraph>
                {t('填寫完成後，請上傳文件進行數據驗證和預覽。')}
                                            </Paragraph>
              <Upload {...uploadProps}>
                <Button 
                  icon={<UploadOutlined />} 
                  size="large"
                  loading={isProcessing}
                >
                  {isProcessing ? t('處理中...') : t('選擇文件上傳')}
                </Button>
              </Upload>
              {uploadedFile && (
                <div>
                  <Text>{t('已選擇文件:')} {uploadedFile.name}</Text>
                  {isProcessing && (
                    <Progress percent={uploadProgress} style={{ marginTop: 8 }} />
                  )}
                </div>
              )}
            </Space>
          </Card>

          <Card title={t('字段說明（必填在前、可選可勾選，用於生成模板）')} style={{ marginTop: 16 }}>
            <Title level={5}>{t('必填字段')}</Title>
            <Row gutter={16} style={{ marginBottom: 12 }}>
              {fieldDescriptions.filter(f => f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space>
                    <Tag color="red">{t('必填')}</Tag>
                    <Text strong>{field.name}</Text>
                  </Space>
                  <div style={{ fontSize: '12px', color: '#666', marginLeft: 16 }}>
                    {field.description}
                  </div>
                </Col>
              ))}
            </Row>
            <Title level={5}>{t('可選字段（勾選後包含在模板中）')}</Title>
            <Row gutter={16}>
              {fieldDescriptions.filter(f => !f.required).map((field) => (
                <Col span={8} key={field.field} style={{ marginBottom: 8 }}>
                  <Space align="start">
                    <Checkbox
                      checked={selectedOptionalKeys.includes(field.field)}
                      onChange={(e) => {
                        const next = e.target.checked
                          ? Array.from(new Set([...selectedOptionalKeys, field.field]))
                          : selectedOptionalKeys.filter(k => k !== field.field);
                        setSelectedOptionalKeys(next);
                      }}
                    />
                    <div>
                      <Space>
                        <Tag>{t('可選')}</Tag>
                        <Text strong>{field.name}</Text>
                      </Space>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {field.description}
                      </div>
                    </div>
                  </Space>
                </Col>
              ))}
            </Row>
          </Card>
        </div>
      )}

      {currentStep === 1 && (
        <div>
          <Alert
            message={t('數據驗證完成，共 {{count}} 行數據', { count: previewData.length })}
            description={
              <Space>
                <span>{t('有效:')} {previewData.filter(item => item.status === 'valid').length}</span>
                <span>{t('警告:')} {previewData.filter(item => item.status === 'warning').length}</span>
                <span>{t('錯誤:')} {previewData.filter(item => item.status === 'error').length}</span>
                <span style={{ marginLeft: '20px', color: '#666' }}>
                  {t('| 按鈕狀態:')} {validationErrors.filter(e => e.severity === 'error').length > 0 || 
                             previewData.filter(item => item.status === 'valid' || item.status === 'warning').length === 0 
                             ? t('🚫 已禁用') : t('✅ 可用')}
                </span>
              </Space>
            }
            type={validationErrors.filter(e => e.severity === 'error').length > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 16 }}
          />

          {validationErrors.length > 0 && (
            <Card 
              title={
                <Space>
                  <span>{t('驗證錯誤')}</span>
                  <Tag color="red">
                    {validationErrors.filter(e => e.severity === 'error').length} {t('個錯誤')}
                                                        </Tag>
                  <Tag color="orange">
                    {validationErrors.filter(e => e.severity === 'warning').length} {t('個警告')}
                                                        </Tag>
                </Space>
              } 
              style={{ marginBottom: 16 }}
            >
              {validationErrors.filter(e => e.severity === 'error').length > 0 && (
                <Alert
                  message={t('存在錯誤項目')}
                  description={t('請修正所有錯誤項目後才能進行導入。警告項目不會阻止導入，但建議檢查。')}
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}
              <Table
                dataSource={validationErrors}
                columns={errorColumns}
                rowKey={(record, index) => `${record.row}-${record.field}-${index}`}
                size="small"
                pagination={{ pageSize: 5 }}
              />
            </Card>
          )}

          <Card title={t('數據預覽')} extra={
            <Space>
              <Button onClick={() => setCurrentStep(0)}>{t('重新上傳')}</Button>
              {(() => {
                const errorCount = validationErrors.filter(e => e.severity === 'error').length;
                const hasErrors = errorCount > 0;
                const validDataCount = previewData.filter(item => item.status === 'valid' || item.status === 'warning').length;
                const isDisabled = validDataCount === 0 || hasErrors;

                console.log('Button state debug:', {
                  hasErrors,
                  validDataCount,
                  isDisabled,
                  validationErrors: validationErrors.length,
                  previewData: previewData.length
                });

                return (
                  <Tooltip
                    title={
                      hasErrors
                        ? t('存在 {{errorCount}} 個錯誤，請修正後重新上傳', { errorCount })
                        : validDataCount === 0
                        ? t('沒有有效的數據可以導入')
                        : t('確認導入數據')
                    }
                  >
                    <Button 
                      type={isDisabled ? 'default' : 'primary'}
                      onClick={isDisabled ? undefined : confirmImport}
                      disabled={isDisabled}
                      style={{
                        backgroundColor: isDisabled ? '#f5f5f5' : undefined,
                        borderColor: isDisabled ? '#d9d9d9' : undefined,
                        color: isDisabled ? 'rgba(0, 0, 0, 0.25)' : undefined,
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        opacity: isDisabled ? 0.6 : 1
                      }}
                      danger={isDisabled}
                    >
                      {isDisabled ? (
                        <>
                          {t('確認導入')}
                                                            </>
                      ) : (
                        t('確認導入')
                      )}
                    </Button>
                  </Tooltip>
                );
              })()}
            </Space>
          }>
            <Table
              dataSource={previewData}
              columns={previewColumns}
              rowKey="row"
              size="small"
              pagination={{ pageSize: 10 }}
              rowClassName={(record) => {
                switch (record.status) {
                  case 'error':
                    return 'row-error';
                  case 'warning':
                    return 'row-warning';
                  case 'valid':
                    return 'row-valid';
                  default:
                    return '';
                }
              }}
            />
          </Card>
        </div>
      )}

      {currentStep === 2 && (
        <Result
          status="success"
          title={t('批量上傳完成！')}
          subTitle={t('成功導入 {{count}} 個出庫訂單，列表已自動刷新', { count: previewData.filter(item => item.status === 'valid' || item.status === 'warning').length })}
          icon={<CheckCircleOutlined />}
          extra={[
            <Button 
              type="primary" 
              key="close"
              onClick={() => {
                resetState();
                onCancel();
              }}
            >
              {t('關閉並返回列表')}
                              </Button>,
            <Button 
              key="continue"
              onClick={() => {
                resetState();
              }}
            >
              {t('繼續上傳')}
                            </Button>
          ]}
        />
      )}

      <style>{`
        .row-error {
          background-color: #fff2f0 !important;
        }
        .row-warning {
          background-color: #fffbe6 !important;
        }
        .row-valid {
          background-color: #f6ffed !important;
        }
        
        /* 强制禁用按钮樣式 */
        .ant-btn-primary[disabled], 
        .ant-btn-primary[disabled]:hover, 
        .ant-btn-primary[disabled]:focus, 
        .ant-btn-primary[disabled]:active {
          background-color: #d9d9d9 !important;
          border-color: #d9d9d9 !important;
          color: #666 !important;
          cursor: not-allowed !important;
          opacity: 0.6 !important;
        }
        
        /* 確保禁用狀態下的樣式覆盖 */
        .ant-btn[disabled] {
          background-color: #f5f5f5 !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.25) !important;
          cursor: not-allowed !important;
          box-shadow: none !important;
        }
      `}</style>
    </Modal>
  );
};

export default BulkUploadModal;